import './PieChart.scss';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DqText } from 'src/components/dq-text';
import { msToTime } from 'src/utils/logs/logHelpers';
import { PieChartController, PieChartDataEntry } from './PieChartController';

interface PieChartProps {
  data: PieChartDataEntry[];
  showTimeDetails: boolean;
}

export const PieChart: React.FC<PieChartProps> = ({ data, showTimeDetails }) => {
  const { formatNumber } = useIntl();
  /** Ref object that contains the element under which the chart is created. */
  const graphRef = useRef<SVGSVGElement>(null);
  /** The controller object managing the svg. */
  const [ctrlRef, setCtrlRef] = useState<PieChartController | null>(null);

  // Effect that creates the svg graph element on mount, and destroys it on unmount.
  useEffect(() => {
    const graph = graphRef.current;
    if (graph) {
      const controller = new PieChartController(graph, showTimeDetails);
      setCtrlRef(controller);
    } else {
      console.warn('The component mounted but chartRef was null! The graph will not draw.');
    }
  }, [showTimeDetails]);

  // Effect that updates the circles each time the data changes.
  useEffect(() => {
    if (data) ctrlRef?.setData(data);
  }, [ctrlRef, data]);

  return (
    <div className="pie-chart-graph">
      <div className="pie-chart-graph-content">
        <svg ref={graphRef} />
      </div>
      <div className="pie-chart-graph-legends">
        {data?.map(d => {
          return (
            <div key={d.key} className={classNames('pie-chart-graph-legend', `ion-color-${d.color}`)}>
              <div className="pie-chart-graph-legend-key">
                <div className="pie-chart-graph-color-key" />
              </div>
              <div>
                <DqText variant="body-strong">{d.label}</DqText>
                {showTimeDetails && <DqText>{msToTime(d.value, formatNumber).replaceAll(', ', ',\n')}</DqText>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
